import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  IconButton,
  useMediaQuery,
  useTheme,
  Modal,
  Box,
  Fade,
  Backdrop
} from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import ZoomIn from "@mui/icons-material/ZoomIn";
import Close from "@mui/icons-material/Close";
import MKBox from "components/MKBox";

const BRAND = {
  main: "#036D3D",
  line: "rgba(3,109,61,.30)",
  light: "rgba(3,109,61,.08)",
};

export default function ProductsCarousel({ images }) {
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up("sm"));
  const upMd = useMediaQuery(theme.breakpoints.up("md"));
  const cols = upMd ? 3 : upSm ? 2 : 1;

  const containerRef = useRef(null);
  const [slideW, setSlideW] = useState(0);
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const pages = Math.max(1, Math.ceil(images.length / Math.max(1, cols)));

  useEffect(() => {
    setPage(0);
    const el = containerRef.current;
    if (el) el.scrollTo({ left: 0 });
  }, [cols, images.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setSlideW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onScroll = () => {
    const el = containerRef.current;
    if (!el || !slideW) return;
    const idx = Math.round(el.scrollLeft / slideW);
    setPage(Math.min(pages - 1, Math.max(0, idx)));
  };

  const goTo = (idx) => {
    const el = containerRef.current;
    if (!el || !slideW) return;
    el.scrollTo({ left: slideW * idx, behavior: "smooth" });
  };
  const next = () => goTo((page + 1) % pages);
  const prev = () => goTo((page - 1 + pages) % pages);

  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || pages <= 1) return;
    const id = setInterval(() => {
      if (slideW) next();
    }, 3500);
    return () => clearInterval(id);
  }, [paused, pages, slideW, page]);

  const groups = useMemo(() => {
    const out = [];
    for (let i = 0; i < images.length; i += cols) out.push(images.slice(i, i + cols));
    return out;
  }, [images, cols]);

  const handleImageClick = (groupIndex, imageIndex) => {
    const globalIndex = groupIndex * cols + imageIndex;
    setSelectedImageIndex(globalIndex);
    setModalOpen(true);
  };

  const handleModalNext = () => {
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const handleModalPrev = () => {
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") handleModalNext();
    if (e.key === "ArrowLeft") handleModalPrev();
    if (e.key === "Escape") setModalOpen(false);
  };

  useEffect(() => {
    if (modalOpen) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [modalOpen]);

  return (
    <>
      <Card
        sx={{
          p: 0,
          borderRadius: 3,
          border: `1px solid ${BRAND.line}`,
          position: "relative",
          overflow: "hidden",
          bgcolor: "#f8f9fa",
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <MKBox
          ref={containerRef}
          onScroll={onScroll}
          sx={{
            display: "flex",
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {groups.map((group, groupIndex) => (
            <MKBox
              key={`page-${groupIndex}`}
              sx={{
                flex: "0 0 100%",
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 2,
                p: 2,
                scrollSnapAlign: "start",
                minHeight: 300,
              }}
            >
              {group.map((src, imageIndex) => (
                <Card
                  key={`${groupIndex}-${imageIndex}-${src}`}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
                    transition: "transform .2s ease, box-shadow .2s ease",
                    cursor: "pointer",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "white",
                    "&:hover": { 
                      transform: "translateY(-3px)", 
                      boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      "& .zoom-overlay": {
                        opacity: 1,
                      }
                    },
                  }}
                  onClick={() => handleImageClick(groupIndex, imageIndex)}
                >
                  <MKBox
                    component="img"
                    src={src}
                    alt={`Peça fabricada ${groupIndex * cols + imageIndex + 1}  – Ramlis Machine`}
                    loading="lazy"
                    sx={{ 
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                      p: 1,
                    }}
                  />
                  <MKBox
                    className="zoom-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: "rgba(0,0,0,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <ZoomIn sx={{ color: "white", fontSize: 32 }} />
                  </MKBox>
                </Card>
              ))}
            </MKBox>
          ))}
        </MKBox>

        {pages > 1 && (
          <>
            <IconButton
              aria-label="Anterior"
              onClick={prev}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 3,
                bgcolor: "rgba(255,255,255,.95)",
                boxShadow: "0 4px 12px rgba(0,0,0,.15)",
                "&:hover": { bgcolor: "#fff" },
                width: 36,
                height: 36,
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              aria-label="Próximo"
              onClick={next}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 3,
                bgcolor: "rgba(255,255,255,.95)",
                boxShadow: "0 4px 12px rgba(0,0,0,.15)",
                "&:hover": { bgcolor: "#fff" },
                width: 36,
                height: 36,
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}

        {pages > 1 && (
          <MKBox sx={{ display: "flex", gap: 1, justifyContent: "center", py: 2 }}>
            {Array.from({ length: pages }).map((_, i) => (
              <MKBox
                key={`dot-${i}`}
                onClick={() => goTo(i)}
                sx={{
                  width: i === page ? 18 : 8,
                  height: 8,
                  borderRadius: 99,
                  bgcolor: i === page ? BRAND.main : "rgba(0,0,0,.18)",
                  transition: "all .25s ease",
                  cursor: "pointer",
                }}
              />
            ))}
          </MKBox>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Fade in={modalOpen} timeout={500}>
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              outline: "none",
            }}
          >
            <IconButton
              onClick={() => setModalOpen(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 10,
                bgcolor: "#fff",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.7)",
                },
              }}
            >
              <Close />
            </IconButton>

            {images.length > 1 && (
              <>
                <IconButton
                  onClick={handleModalPrev}
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    bgcolor: "#fff",
                    color: "white",
                    "&:hover": {
                      bgcolor: "rgba(0,0,0,0.7)",
                    },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  onClick={handleModalNext}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    bgcolor: "rgba(255, 255, 255, 1)",
                    color: "white",
                    "&:hover": {
                      bgcolor: "rgba(255, 247, 247, 1)",
                    },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </>
            )}

            <Box
              component="img"
              src={images[selectedImageIndex]}
              alt={`Peça fabricada ${selectedImageIndex + 1} – Ramlis Machine`}
              sx={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: 1,
                boxShadow: 24,
                bgcolor: "white",
                p: 1,
              }}
            />

            {images.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 20,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "#fff",
                  color: "white",
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontSize: "0.875rem",
                }}
              >
                {selectedImageIndex + 1} / {images.length}
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

ProductsCarousel.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ).isRequired,
};